import { useApiClient } from '@chilipiper/api-client/src/context'
import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query'
import { UserAvailabilityApiClientV1 } from '@chilipiper/api-client/src/user-availability'
import { unique } from 'remeda'
import {
  AvailabilityOptions,
  isUsersCombinedMeetingStartTimes,
  MeetingStartTimes,
  RequestAvailabilityOptions,
  SlotsMutationOptions,
} from '../../load-availability-options'
import { getAvailability } from '../helpers/get-availability'
import { AvailabilitySpan } from '../types'
import { useMeetingsAvailabilitySpan } from './use-meeting-availability-span'

const getUserIdsFromAvailabilityOptions = (availabilityOptions?: AvailabilityOptions): string[] => {
  if (!availabilityOptions) return []
  if ('userIds' in availabilityOptions) return availabilityOptions.userIds
  if ('distributionAttendees' in availabilityOptions)
    return availabilityOptions.distributionAttendees.flatMap(item => item.userIds)
  return []
}

const slotsQuery = (
  params: SlotsMutationOptions & {
    availabilitySpan?: AvailabilitySpan
    userAvailabilityClient: UserAvailabilityApiClientV1
  }
) => {
  const requiredIds = getUserIdsFromAvailabilityOptions(params.availabilityOptions).concat(
    params.additionalRequiredAttendees ?? []
  )
  const feature = params.feature ? [params.feature] : []

  return queryOptions<MeetingStartTimes>({
    queryKey: [
      'availability',
      params.meetingTypeId,
      requiredIds,
      ...feature,
      params.intervalStartsAt || params.availabilitySpan?.startsAt || 'initial-start-time',
      'user-availability/availability',
    ],
    enabled: Boolean(params.meetingTypeId && params.availabilitySpan),
    queryFn:
      params.availabilitySpan && params.availabilityOptions
        ? () =>
            getAvailability({
              ...params,
              ...(params.availabilityOptions as AvailabilityOptions),
            }).then(res => res.timeSlots)
        : skipToken,
  })
}

export const useSlots = (options: SlotsMutationOptions) => {
  const { data: availabilitySpan } = useMeetingsAvailabilitySpan({
    tenantId: options.tenantId,
    meetingTypeId: options.meetingTypeId,
    meetingTypeVersion: options.meetingTypeVersion ?? new Date().toISOString(),
  })
  const { userAvailability } = useApiClient()
  const queryClient = useQueryClient()
  const queryConfig = slotsQuery({
    ...options,
    availabilitySpan,
    userAvailabilityClient: userAvailability,
  })

  const {
    data,
    error,
    isPending: isLoadingData,
    isFetchedAfterMount,
    refetch,
  } = useQuery(queryConfig)

  const { mutateAsync: onUpdateTimeSlots, isPending: isMutating } = useMutation({
    mutationFn: async (arg: RequestAvailabilityOptions) => {
      if (!options.availabilityOptions) {
        throw new Error('Availability options are required')
      }
      const availabilityOptions = options.availabilityOptions
      return getAvailability({
        ...arg,
        availabilitySpan,
        ...availabilityOptions,
        userAvailabilityClient: userAvailability,
      }).then(res => res.timeSlots)
    },
    onSuccess: newSlots => {
      queryClient.setQueryData(queryConfig.queryKey, (current): MeetingStartTimes | undefined => {
        if (
          (current === undefined || isUsersCombinedMeetingStartTimes(current)) &&
          isUsersCombinedMeetingStartTimes(newSlots)
        ) {
          return {
            ...newSlots,
            startTimes: unique([...(current?.startTimes ?? []), ...(newSlots.startTimes ?? [])]),
          } satisfies MeetingStartTimes
        } else if (
          isUsersCombinedMeetingStartTimes(current) ||
          isUsersCombinedMeetingStartTimes(newSlots)
        ) {
          return current
        }
        return {
          ...newSlots,
          startTimes: unique([...(current?.startTimes ?? []), ...(newSlots.startTimes ?? [])]),
        } satisfies MeetingStartTimes
      })
    },
  })

  return {
    data,
    onUpdateTimeSlots,
    isLoading: isMutating || isLoadingData,
    isFetchedAfterMount,
    hasError: !!error,
    error,
    refetchSlots: refetch,
  }
}
